import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ShoppingCart,
  TrendingDown,
  ClipboardList,
  Truck,
  BarChart3,
  AlertTriangle,
  Lightbulb,
  BookOpen,
  List,
  Scale,
  PackageCheck,
  FileSpreadsheet,
  Zap,
  Calendar,
  Handshake,
  Search,
} from 'lucide-react';

/* ─── FAQs ───────────────────────────────────────────── */

const faqs = [
  {
    q: '¿Cual es el porcentaje ideal de gasto en compras sobre la facturacion?',
    a: 'Para la mayoria de restaurantes, las compras de materia prima deben representar entre el 25% y el 32% de la facturacion. Si tu restaurante supera el 35%, es urgente revisar proveedores, precios, mermas y desperdicio. Con un sistema de gestion de compras como PLEKO, puedes monitorizar este porcentaje en tiempo real y actuar antes de que se descontrole.',
  },
  {
    q: '¿Con cuantos proveedores deberia trabajar para cada categoria de producto?',
    a: 'Lo ideal es tener al menos 2-3 proveedores por categoria principal (carne, pescado, verdura, secos). No necesitas comprar siempre al mas barato, pero tener alternativas te da poder de negociacion y te protege ante problemas de suministro. Compara precios regularmente usando datos de mercados mayoristas como Mercabarna o Mercamadrid.',
  },
  {
    q: '¿Como puedo saber si mi proveedor me esta cobrando un precio justo?',
    a: 'Compara sus precios con los de mercados mayoristas (Mercabarna, Mercamadrid). Es normal que un distribuidor cobre un 15-25% mas que el precio de lonja, porque incluye transporte y servicio. Pero si la diferencia supera el 30-40%, estas pagando de mas. PLEKO compara automaticamente tus precios de compra con los de mercado mayorista.',
  },
  {
    q: '¿Cada cuanto debo hacer pedidos a proveedores?',
    a: 'Depende del tipo de producto. Producto fresco (verdura, pescado): 2-3 veces por semana. Carnes: 2 veces por semana. Secos y envasados: semanal o quincenal. Bebidas: semanal o quincenal. La clave es encontrar el equilibrio entre frescura, stock minimo y coste de transporte. Pedidos demasiado frecuentes aumentan los costes logisticos; demasiado espaciados generan exceso de stock.',
  },
  {
    q: '¿Que es un punto de pedido y como lo calculo?',
    a: 'El punto de pedido es el nivel minimo de stock al que un ingrediente debe llegar antes de hacer un nuevo pedido. Se calcula asi: Punto de pedido = Consumo diario medio x Plazo de entrega (dias) + Stock de seguridad. Ejemplo: si usas 5 kg de pollo/dia, tu proveedor tarda 2 dias en entregar y quieres 1 dia de seguridad: 5 x 2 + 5 = 15 kg. Cuando tu stock de pollo baje de 15 kg, haz pedido.',
  },
  {
    q: '¿Es mejor negociar precio o condiciones de pago con los proveedores?',
    a: 'Ambos son importantes, pero el precio tiene impacto directo en tu food cost. Negocia primero el precio unitario, especialmente en productos de alto volumen. Las condiciones de pago (30, 60, 90 dias) mejoran tu flujo de caja pero no reducen el coste. Si puedes, ofrece pago anticipado a cambio de un descuento del 3-5%: suele ser mas rentable que financiarte con un banco.',
  },
  {
    q: '¿Como evito el exceso de stock en mi restaurante?',
    a: 'Las tres claves son: 1) Definir stock maximo y minimo para cada ingrediente. 2) Hacer pedidos basados en consumo real (no "por si acaso"). 3) Revisar inventario semanalmente para detectar producto acumulado. El exceso de stock genera mermas por caducidad, ocupa espacio valioso y congela dinero. Un restaurante medio tiene entre 2.000 y 5.000 euros "atrapados" en stock innecesario.',
  },
  {
    q: '¿Merece la pena usar software para gestionar las compras del restaurante?',
    a: 'Si. Un restaurante que factura 30.000 euros/mes y reduce sus compras un 5% ahorra 525 euros mensuales solo en materia prima. El plan BASIC de PLEKO cuesta 49 euros/mes, lo que supone un ROI de 10x desde el primer mes. Ademas, ahorras 3-5 horas semanales en gestion de pedidos, comparacion de precios y control de albaranes.',
  },
];

/* ─── Steps for efficient purchasing system ────────── */

const steps = [
  { num: 1, title: 'Audita tus compras actuales', desc: 'Recopila albaranes y facturas de los ultimos 3 meses. Calcula cuanto gastas por categoria (carne, pescado, verdura, secos, bebidas). Identifica los 10 ingredientes que mas dinero te cuestan.' },
  { num: 2, title: 'Crea una ficha de cada proveedor', desc: 'Registra nombre, productos, precios actualizados, condiciones de pago, plazo de entrega, pedido minimo y datos de contacto. Ten al menos 2 proveedores por categoria.' },
  { num: 3, title: 'Define stock minimo y punto de pedido', desc: 'Para cada ingrediente de alto consumo, calcula el stock minimo (consumo diario x plazo de entrega + margen de seguridad). Cuando el stock baje de ese nivel, haz pedido automaticamente.' },
  { num: 4, title: 'Planifica pedidos desde la carta', desc: 'Usa los escandallos de tus recetas para calcular exactamente cuanto necesitas comprar segun la prevision de cubiertos. Compra lo que vas a usar, no lo que "crees" que necesitas.' },
  { num: 5, title: 'Compara precios sistematicamente', desc: 'Antes de cada pedido grande, compara los precios de tus proveedores con los de mercados mayoristas. Una diferencia del 10% en un ingrediente que compras a diario supone cientos de euros al mes.' },
  { num: 6, title: 'Controla la recepcion de mercancia', desc: 'Verifica cada entrega: peso correcto, temperatura adecuada, estado visual, fecha de caducidad, coincidencia con el albaran. Un 5-10% de las entregas tienen discrepancias que pasan desapercibidas.' },
];

/* ─── Savings table data ─────────────────────────────── */

const savingsData = [
  { mejora: 'Comparar precios entre proveedores', ahorro: '1.500-4.000', dificultad: 'Baja' },
  { mejora: 'Negociar con datos de mercado', ahorro: '1.000-3.000', dificultad: 'Media' },
  { mejora: 'Eliminar exceso de stock', ahorro: '800-2.500', dificultad: 'Baja' },
  { mejora: 'Control de recepcion de mercancia', ahorro: '500-1.500', dificultad: 'Baja' },
  { mejora: 'Planificar pedidos desde escandallos', ahorro: '1.000-3.000', dificultad: 'Media' },
  { mejora: 'Comprar producto de temporada', ahorro: '1.000-3.000', dificultad: 'Baja' },
  { mejora: 'Rotacion FIFO / stock minimo', ahorro: '600-2.000', dificultad: 'Baja' },
  { mejora: 'Digitalizar gestion de compras', ahorro: '500-1.500', dificultad: 'Baja' },
];

/* ─── Table of Contents ──────────────────────────────── */

const toc = [
  { id: 'por-que', label: 'Por que la gestion de compras es clave para la rentabilidad' },
  { id: 'errores', label: 'Los 5 errores mas comunes en compras' },
  { id: 'sistema-compras', label: 'Como crear un sistema de compras eficiente' },
  { id: 'ficha-proveedor', label: 'La ficha de proveedor: que datos registrar' },
  { id: 'negociar', label: 'Como negociar con proveedores' },
  { id: 'precios-mercado', label: 'Comparar precios de mercado mayorista' },
  { id: 'planificador', label: 'El planificador de compras: de la carta al pedido' },
  { id: 'tabla-ahorro', label: 'Tabla: ahorro estimado por mejora en compras' },
  { id: 'temporada', label: 'Productos de temporada = mejor precio y calidad' },
  { id: 'recepcion', label: 'Control de recepcion de mercancia' },
  { id: 'stock', label: 'Gestion de stock: FIFO, punto de pedido, stock minimo' },
  { id: 'software-vs-excel', label: 'Software vs Excel para gestionar compras' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* ─── Schema.org JSON-LD ─────────────────────────────── */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Gestion de Compras en Restaurantes: Como Optimizar Pedidos y Reducir Costes',
  description:
    'Guia completa para optimizar las compras de tu restaurante: negociacion con proveedores, comparacion de precios, control de stock, planificacion de pedidos y ahorro estimado.',
  author: { '@type': 'Organization', name: 'PLEKO' },
  publisher: {
    '@type': 'Organization',
    name: 'PLEKO',
    logo: { '@type': 'ImageObject', url: 'https://pleko.app/favicon.svg' },
  },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  image: 'https://pleko.app/og-image.png',
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Gestion de Compras en Restaurantes',
      item: 'https://pleko.app/gestion-compras-restaurante',
    },
  ],
};

/* ─── Component ──────────────────────────────────────── */

export default function GestionComprasRestaurantePage() {
  return (
    <>
      <Helmet>
        <title>Gestion de Compras en Restaurantes: Como Optimizar Pedidos y Reducir Costes | PLEKO</title>
        <meta
          name="description"
          content="Guia practica para optimizar la gestion de compras de tu restaurante. Aprende a negociar con proveedores, comparar precios de mercado, controlar stock y reducir costes de materia prima entre un 5% y un 15%."
        />
        <meta
          name="keywords"
          content="gestion compras restaurante, compras restaurante, optimizar pedidos restaurante, gestion proveedores hosteleria, reducir costes compras restaurante, negociar proveedores restaurante, control stock restaurante, planificador compras hosteleria, comparar precios proveedores"
        />
        <link rel="canonical" href="https://pleko.app/gestion-compras-restaurante" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/gestion-compras-restaurante" />
        <meta
          property="og:title"
          content="Gestion de Compras en Restaurantes: Como Optimizar Pedidos y Reducir Costes"
        />
        <meta
          property="og:description"
          content="Guia completa para optimizar compras en hosteleria. Negociacion con proveedores, precios de mercado, control de stock y ahorro estimado de 6.900-20.500 euros/ano."
        />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Gestion de Compras en Restaurantes | PLEKO"
        />
        <meta
          name="twitter:description"
          content="Como optimizar las compras de tu restaurante y reducir costes de materia prima entre un 5% y un 15%."
        />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* ═══════════ HERO ═══════════ */}
        <section className="bg-gradient-to-b from-indigo-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium mb-6">
                <ShoppingCart className="w-4 h-4" />
                Guia de gestion de compras para hosteleria — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Gestion de Compras en Restaurantes:{' '}
                <span className="text-indigo-600">Como Optimizar Pedidos y Reducir Costes</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Las compras representan entre el <strong>28% y el 35% de la facturacion</strong> de un restaurante. Optimizar esta partida puede suponer un ahorro de 8.750 euros anuales en un restaurante que factura 500.000 euros/ano. Aprende a comprar mejor con datos, no con intuicion.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#sistema-compras"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
                >
                  Ver el sistema de compras
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Probar PLEKO gratis
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ═══════════ CONTENT ═══════════ */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-indigo-600" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">
            {/* ═══════════ SECTION 1: POR QUE ═══════════ */}
            <h2 id="por-que" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Por que la gestion de compras es clave para la rentabilidad
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La materia prima es la partida de gasto mas grande y mas variable de un restaurante. Representa entre el <strong>28% y el 35% de la facturacion</strong>, y es la que mas rapidamente puedes optimizar con las decisiones correctas. A diferencia del alquiler (fijo) o el personal (dificil de ajustar a corto plazo), las compras dependen directamente de tus decisiones diarias.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Un restaurante medio en Espana factura entre 300.000 y 600.000 euros al ano. Si sus compras representan el 32% de la facturacion, esta gastando entre 96.000 y 192.000 euros anuales en materia prima. <strong>Reducir ese porcentaje solo un 5% supone un ahorro de entre 4.800 y 9.600 euros al ano</strong> — dinero que va directamente al beneficio neto.
            </p>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6 mb-10">
              <p className="text-indigo-800 font-semibold text-center mb-2">El impacto de optimizar las compras</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                <div className="text-center p-3 bg-white rounded-lg border border-indigo-100">
                  <p className="text-xs text-slate-500 uppercase">Facturacion anual</p>
                  <p className="text-xl font-bold text-slate-800">500.000 euros</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-indigo-100">
                  <p className="text-xs text-slate-500 uppercase">Ahorro del 5% en compras</p>
                  <p className="text-xl font-bold text-indigo-600">8.750 euros/ano</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg border border-indigo-100">
                  <p className="text-xs text-slate-500 uppercase">Equivale a</p>
                  <p className="text-xl font-bold text-green-600">+729 euros/mes</p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 2: 5 ERRORES ═══════════ */}
            <h2 id="errores" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Los 5 errores mas comunes en compras de restaurante
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La mayoria de restaurantes no pierden dinero por un solo error grave, sino por una acumulacion de pequenas ineficiencias en sus compras. Estos son los cinco errores que vemos con mas frecuencia:
            </p>

            <div className="space-y-4 mb-10">
              {[
                { num: 1, title: 'Comprar sin lista ni planificacion', desc: 'Pedir "a ojo" o "lo de siempre" sin calcular las necesidades reales segun la prevision de cubiertos. Resultado: exceso de stock en unos productos y roturas en otros.' },
                { num: 2, title: 'No comparar precios entre proveedores', desc: 'Usar un solo proveedor para todo por comodidad sin contrastar sus precios con alternativas ni con mercados mayoristas. Puedes estar pagando un 15-25% mas sin saberlo.' },
                { num: 3, title: 'Exceso de stock y producto acumulado', desc: 'Comprar en exceso "por si acaso" genera mermas por caducidad, congela capital y ocupa espacio en camara. Un restaurante medio tiene 2.000-5.000 euros atrapados en stock innecesario.' },
                { num: 4, title: 'No negociar con proveedores', desc: 'Aceptar los precios del catalogo sin negociar. Los proveedores esperan negociacion — si no negocias, estas pagando el precio mas alto del mercado.' },
                { num: 5, title: 'No revisar los albaranes al recibir mercancia', desc: 'El 5-10% de las entregas tienen discrepancias: producto que falta, peso incorrecto, calidad inferior a la pedida o precio diferente al pactado. Si no lo verificas, lo asumes.' },
              ].map(({ num, title, desc }) => (
                <div key={num} className="flex gap-4 p-5 bg-red-50 rounded-xl border border-red-100">
                  <div className="flex-shrink-0">
                    <span className="w-8 h-8 rounded-full bg-red-500 text-white font-bold flex items-center justify-center text-sm">{num}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">{title}</h3>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 3: SISTEMA COMPRAS ═══════════ */}
            <h2 id="sistema-compras" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Como crear un sistema de compras eficiente en 6 pasos
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Un buen sistema de compras no es complicado, pero requiere disciplina y datos fiables. Estos son los seis pasos para pasar de comprar por intuicion a comprar con criterio:
            </p>

            <div className="space-y-6 mb-10">
              {steps.map(({ num, title, desc }) => (
                <div key={num} className="flex gap-4">
                  <div className="flex-shrink-0">
                    <span className="w-10 h-10 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center text-lg">{num}</span>
                  </div>
                  <div className="border-l-4 border-indigo-200 pl-5 flex-1">
                    <h3 className="text-lg font-bold text-slate-800 mb-2">Paso {num}: {title}</h3>
                    <p className="text-slate-700 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-indigo-800">Automatiza con PLEKO</p>
                  <p className="text-indigo-700 text-sm mt-1">
                    PLEKO conecta tus escandallos con el planificador de compras: introduces la prevision de cubiertos y te genera la lista de compra exacta con las cantidades necesarias de cada ingrediente. Ademas, <Link to="/ocr-facturas-restaurante" className="text-indigo-800 underline hover:text-indigo-900">escanea albaranes con OCR</Link> para actualizar precios automaticamente.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 4: FICHA PROVEEDOR ═══════════ */}
            <h2 id="ficha-proveedor" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              La ficha de proveedor: que datos registrar
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Cada proveedor con el que trabajas deberia tener una ficha completa y actualizada. Estos son los datos esenciales que necesitas para gestionar tus compras de forma profesional:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Dato</th>
                    <th className="p-3 text-left font-medium">Ejemplo</th>
                    <th className="p-3 text-left font-medium">Por que importa</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Nombre y contacto', 'Distribuciones Martinez — 934 567 890', 'Para pedidos urgentes y reclamaciones'],
                    ['Productos que suministra', 'Carnes frescas, embutidos', 'Para saber a quien pedir cada producto'],
                    ['Precios actualizados', 'Solomillo 24,50 euros/kg (junio 2026)', 'Para comparar y negociar'],
                    ['Condiciones de pago', '30 dias factura', 'Para gestionar el flujo de caja'],
                    ['Plazo de entrega', '24h (pedidos antes de las 14h)', 'Para calcular punto de pedido'],
                    ['Pedido minimo', '150 euros o 20 kg', 'Para planificar pedidos agrupados'],
                    ['Dias de entrega', 'Lunes, miercoles, viernes', 'Para cuadrar la planificacion semanal'],
                    ['Valoracion / notas', 'Buena calidad, a veces retrasa', 'Para tomar decisiones de cambio'],
                  ].map(([dato, ejemplo, motivo], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{dato}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{ejemplo}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500 text-sm">{motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              En PLEKO puedes gestionar todos tus proveedores con fichas completas, historico de precios y comparativas automaticas. Cada vez que escaneas un albaran, los precios del proveedor se actualizan automaticamente en el sistema.
            </p>

            {/* ═══════════ SECTION 5: NEGOCIACION ═══════════ */}
            <h2 id="negociar" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <Handshake className="w-8 h-8 text-indigo-600" />
                Como negociar con proveedores: 5 tacticas efectivas
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Negociar con proveedores no es pedir descuentos sin mas — es crear una relacion comercial donde ambas partes ganan. Estas son las cinco tacticas que mejor funcionan en hosteleria:
            </p>

            <div className="space-y-4 mb-10">
              {[
                { num: 1, icon: BarChart3, title: 'Negocia por volumen', desc: 'Compromete un volumen mensual a cambio de un precio unitario mejor. Ejemplo: "Si te garantizo 200 kg de pollo al mes, ¿puedes bajar de 6,50 a 5,90 euros/kg?". Es la tactica mas directa y la que los proveedores aceptan con mas facilidad.' },
                { num: 2, icon: TrendingDown, title: 'Ofrece pago anticipado', desc: 'Si tienes liquidez, ofrece pagar a 7 dias (o al contado) a cambio de un descuento del 3-5%. Los proveedores valoran mucho la seguridad de cobro rapido, y ese 3-5% es dinero que se ahorra directamente en tu food cost.' },
                { num: 3, icon: ClipboardList, title: 'Exclusividad parcial', desc: 'Ofrece que sea tu proveedor principal en una categoria (ej: "todo el pescado contigo") a cambio de precios preferenciales. El proveedor gana volumen asegurado y tu ganas precio.' },
                { num: 4, icon: Calendar, title: 'Aprovecha la temporalidad', desc: 'Negocia precios fijos trimestrales para productos estacionales. Cuando el precio de mercado esta bajo, cierra precio para los proximos meses. Protege tu margen contra subidas imprevistas.' },
                { num: 5, icon: Search, title: 'Usa datos de mercado como palanca', desc: '"Mercabarna cotiza la dorada a 9,20 euros/kg y tu me la facturas a 12,50 euros/kg — un 36% mas. ¿Podemos acercarnos a 10,50?" Los datos concretos son mucho mas persuasivos que "me parece caro".' },
              ].map(({ num, icon: Icon, title, desc }) => (
                <div key={num} className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-100">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-indigo-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">{num}. {title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 6: PRECIOS MERCADO ═══════════ */}
            <h2 id="precios-mercado" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Comparar precios de mercado mayorista
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Los mercados mayoristas como <strong>Mercabarna</strong> (Barcelona) y <strong>Mercamadrid</strong> (Madrid) publican precios de referencia que puedes usar para evaluar si tus proveedores te cobran un precio justo. Es la herramienta de negociacion mas poderosa que existe en hosteleria.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Un distribuidor normalmente cobra entre un 15% y un 25% mas que el precio de lonja — es razonable porque incluye transporte, seleccion y servicio. Pero si la diferencia supera el 30-40%, estas pagando de mas y tienes margen para negociar.
            </p>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <BarChart3 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-indigo-800">Compara tus precios en PLEKO</p>
                  <p className="text-indigo-700 text-sm mt-1">
                    PLEKO compara automaticamente los precios de tus ingredientes con los de <Link to="/precios-mercado" className="text-indigo-800 underline hover:text-indigo-900">Mercabarna y Mercamadrid</Link>. Cada ingrediente muestra la diferencia porcentual entre tu precio de compra y el precio de mercado mayorista, para que sepas exactamente donde tienes margen de negociacion.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 7: PLANIFICADOR ═══════════ */}
            <h2 id="planificador" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              El planificador de compras: de la carta al pedido
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El metodo mas eficiente para calcular tus compras es partir de la <Link to="/escandallo" className="text-indigo-600 hover:underline">carta escandallada</Link>: si sabes cuantos cubiertos vas a hacer y que platos vas a servir, puedes calcular exactamente cuantos kilos de cada ingrediente necesitas. Asi compras lo justo — ni de mas (merma) ni de menos (roturas de stock).
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El proceso es simple: prevision de cubiertos x mix de ventas por plato x ingredientes por racion = lista de compra exacta. Lo que en Excel te lleva horas, un software como PLEKO lo calcula en segundos y te genera el pedido por proveedor.
            </p>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-10">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-indigo-600" /> Ejemplo: planificacion de compras semanal
              </h3>
              <div className="space-y-3 text-sm text-slate-700">
                <div className="flex items-start gap-2">
                  <span className="font-bold text-indigo-600 flex-shrink-0">1.</span>
                  <span>Prevision: 400 cubiertos esta semana (80/dia de media)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-indigo-600 flex-shrink-0">2.</span>
                  <span>Mix de ventas: 25% lubina, 20% entrecot, 15% risotto, 10% ensalada, 30% otros</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-indigo-600 flex-shrink-0">3.</span>
                  <span>Lubina: 100 raciones x 300 g brutos/racion = 30 kg de lubina entera</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-indigo-600 flex-shrink-0">4.</span>
                  <span>Pedido al proveedor: 30 kg lubina entera (2 entregas: lunes 18 kg + jueves 12 kg)</span>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 8: TABLA AHORRO ═══════════ */}
            <h2 id="tabla-ahorro" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Tabla: ahorro estimado por cada mejora en compras
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Estas son las principales areas de mejora en la gestion de compras de un restaurante tipo que factura entre 25.000 y 40.000 euros/mes, con su ahorro anual estimado:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Mejora</th>
                    <th className="p-3 text-right font-medium">Ahorro anual estimado</th>
                    <th className="p-3 text-center font-medium">Dificultad</th>
                  </tr>
                </thead>
                <tbody>
                  {savingsData.map(({ mejora, ahorro, dificultad }, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-medium text-slate-800">{mejora}</td>
                      <td className="p-3 border-b border-slate-100 text-right font-semibold text-indigo-600">{ahorro} euros</td>
                      <td className="p-3 border-b border-slate-100 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${dificultad === 'Baja' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                          {dificultad}
                        </span>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-indigo-50">
                    <td className="p-3 font-bold text-slate-800 text-base">TOTAL ESTIMADO</td>
                    <td className="p-3 text-right font-bold text-indigo-600 text-lg">6.900-20.500 euros</td>
                    <td className="p-3" />
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Nota sobre los calculos</p>
                  <p className="text-amber-700 text-sm mt-1">
                    Los ahorros no son acumulativos al 100% — hay solapamientos entre mejoras. Un ahorro realista aplicando todas las mejoras es del <strong>5-15% del gasto total en compras</strong>. Para un restaurante que gasta 10.000 euros/mes en materia prima, eso supone entre 6.000 y 18.000 euros/ano de ahorro real.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 9: TEMPORADA ═══════════ */}
            <h2 id="temporada" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <Calendar className="w-8 h-8 text-indigo-600" />
                Productos de temporada = mejor precio y calidad
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Comprar producto de temporada es una de las formas mas inteligentes de reducir costes y mejorar la calidad de tus platos al mismo tiempo. Los productos fuera de temporada cuestan entre un <strong>30% y un 100% mas</strong> que en su momento optimo, y ademas llegan con menos sabor, peor textura y mayor huella de transporte.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Adaptar tu carta a la temporada no significa cambiarla completamente cada mes. Basta con sustituir 5-10 ingredientes clave por alternativas de temporada para notar una reduccion significativa en el food cost y una mejora en la percepcion del cliente.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-800">Consulta el calendario de productos de temporada</p>
                  <p className="text-green-700 text-sm mt-1">
                    Hemos creado un <Link to="/calendario-temporada" className="text-green-800 underline hover:text-green-900">calendario interactivo con 86 productos espanoles de temporada</Link> (frutas, verduras, pescados, mariscos, setas y caza) para que puedas planificar tus compras y tu carta con los mejores productos de cada estacion.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 10: RECEPCION ═══════════ */}
            <h2 id="recepcion" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <PackageCheck className="w-8 h-8 text-indigo-600" />
                Control de recepcion de mercancia
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Cada entrega de proveedor debe verificarse antes de firmar el albaran. El 5-10% de las entregas tienen alguna discrepancia que, si no se detecta, se convierte en un coste oculto. Estos son los puntos a comprobar en cada recepcion:
            </p>

            <div className="space-y-3 mb-10">
              {[
                { check: 'Peso correcto', desc: 'Pesa las cajas y compara con lo indicado en el albaran. Especialmente importante en carne y pescado, donde las diferencias de 200-500 g por caja son frecuentes.' },
                { check: 'Temperatura adecuada', desc: 'Refrigerados: 0-4°C. Congelados: por debajo de -18°C. Si llega fuera de rango, rechaza la mercancia. Es un riesgo de seguridad alimentaria y de calidad.' },
                { check: 'Estado visual del producto', desc: 'Color, olor, frescura, envase intacto. Un pescado con ojos hundidos o branquias marrones no es fresco, independientemente de lo que diga la etiqueta.' },
                { check: 'Fecha de caducidad o consumo preferente', desc: 'Rechaza producto con menos de 3 dias de margen (frescos) o menos de 1 mes (envasados). No compres tiempo de camara que no vas a usar.' },
                { check: 'Coincidencia albaran-pedido', desc: 'Verifica que lo entregado coincide con lo pedido: productos, cantidades, calidades y precios. Las sustituciones no pactadas son frecuentes.' },
              ].map(({ check, desc }) => (
                <div key={check} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-medium text-slate-800">{check}</span>
                    <p className="text-slate-600 text-sm mt-1">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ═══════════ SECTION 11: STOCK ═══════════ */}
            <h2 id="stock" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              <span className="flex items-center gap-3">
                <Scale className="w-8 h-8 text-indigo-600" />
                Gestion de stock: FIFO, punto de pedido, stock minimo
              </span>
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La gestion de stock es inseparable de la gestion de compras. Comprar bien no sirve de nada si despues almacenas mal. Los tres conceptos clave que todo restaurante debe dominar son:
            </p>

            <div className="space-y-6 mb-6">
              <div className="border-l-4 border-indigo-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">FIFO (First In, First Out)</h3>
                <p className="text-slate-700 leading-relaxed">
                  Lo que entra primero se usa primero. Coloca el producto nuevo detras del existente en la camara. Parece obvio, pero en la practica muchos restaurantes apilan producto nuevo encima del viejo y acaban tirando comida caducada. Una buena rotacion FIFO puede reducir el desperdicio por caducidad entre un 30% y un 50%.
                </p>
              </div>
              <div className="border-l-4 border-indigo-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">Punto de pedido</h3>
                <p className="text-slate-700 leading-relaxed">
                  El nivel de stock al que debes hacer un nuevo pedido. Se calcula como: <strong>consumo diario medio x plazo de entrega + stock de seguridad</strong>. Si consumes 3 kg de salmon al dia, tu proveedor tarda 2 dias en entregar y quieres 1 dia de seguridad: punto de pedido = 3 x 2 + 3 = 9 kg. Cuando tu stock de salmon baje de 9 kg, haz pedido.
                </p>
              </div>
              <div className="border-l-4 border-indigo-400 pl-5">
                <h3 className="text-lg font-bold text-slate-800 mb-2">Stock minimo y maximo</h3>
                <p className="text-slate-700 leading-relaxed">
                  Define para cada ingrediente importante el stock minimo (por debajo del cual hay riesgo de rotura) y el stock maximo (por encima del cual hay exceso). El stock maximo evita sobrecomprar en ofertas puntuales que despues generan merma. La formula: stock maximo = consumo semanal + stock de seguridad.
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Error comun: comprar "ofertas" que acaban en la basura</p>
                  <p className="text-amber-700 text-sm mt-1">
                    "Mi proveedor me ofrece 50 kg de salmon a 12 euros/kg en vez de 18 euros/kg. ¡Compro!" Pero si tu consumo semanal es de 15 kg y el salmon dura 5 dias en camara, acabaras tirando 20 kg. El "ahorro" de 120 euros se convierte en una perdida de 240 euros. Calcula siempre antes de comprar.
                  </p>
                </div>
              </div>
            </div>

            {/* ═══════════ SECTION 12: SOFTWARE VS EXCEL ═══════════ */}
            <h2 id="software-vs-excel" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Software vs Excel para gestionar compras
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Hay tres formas de gestionar las compras de un restaurante. Cada una tiene sus ventajas segun el tamano de tu negocio y tu nivel de digitalizacion:
            </p>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-slate-400" /> Excel / hojas de calculo
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Gratuito y conocido. Pero todo es manual: actualizar precios, comparar proveedores, calcular pedidos. Con mas de 50 ingredientes se vuelve inmanejable.
                </p>
                <ul className="space-y-1 text-sm text-slate-600">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Gratis</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">x</span>Todo manual</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">x</span>Sin comparacion de precios</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">x</span>Errores frecuentes</li>
                </ul>
              </div>
              <div className="border border-slate-200 rounded-xl p-5">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <Truck className="w-5 h-5 text-slate-400" /> ERP generico
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  Cubre facturacion y contabilidad, pero no esta disenado para hosteleria. No conecta compras con escandallos ni compara con mercados mayoristas.
                </p>
                <ul className="space-y-1 text-sm text-slate-600">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Bueno para contabilidad</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">x</span>Sin escandallos</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">x</span>Sin precios de mercado</li>
                  <li className="flex gap-2"><span className="text-red-400 flex-shrink-0">x</span>Caro (150-300 euros/mes)</li>
                </ul>
              </div>
              <div className="border-2 border-indigo-300 bg-indigo-50 rounded-xl p-5">
                <h3 className="font-bold text-indigo-700 mb-3 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-indigo-500" /> PLEKO (especializado)
                </h3>
                <p className="text-sm text-slate-700 mb-3">
                  Disenado para hosteleria. Conecta proveedores, precios de mercado, escandallos, planificador de compras y OCR de albaranes en una sola plataforma.
                </p>
                <ul className="space-y-1 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Comparacion de precios automatica</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />OCR de albaranes</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Planificador de compras</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Desde 49 euros/mes</li>
                </ul>
                <Link to="/software-gestion-restaurante" className="text-sm text-indigo-700 font-semibold hover:underline block mt-3">
                  Ver PLEKO en detalle →
                </Link>
              </div>
            </div>
          </div>

          {/* ═══════════ FAQ ═══════════ */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Preguntas frecuentes sobre gestion de compras en restaurantes
            </h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4 flex-shrink-0">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* ═══════════ INTERNAL LINKS ═══════════ */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-indigo-600" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                { to: '/control-costes-restaurante', label: 'Control de costes en restaurantes' },
                { to: '/rentabilidad-restaurante', label: 'Rentabilidad en restaurantes' },
                { to: '/precios-mercado', label: 'Precios de mercado mayorista en tiempo real' },
                { to: '/calendario-temporada', label: 'Calendario de productos de temporada' },
                { to: '/ocr-facturas-restaurante', label: 'OCR de facturas y albaranes' },
                { to: '/mermas-cocina', label: 'Mermas en cocina: guia completa' },
                { to: '/tabla-mermas-alimentos', label: 'Tabla de mermas por alimento' },
                { to: '/software-gestion-restaurante', label: 'Software de gestion para restaurantes' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-800 hover:underline">
                  <ArrowRight className="w-3 h-3 flex-shrink-0" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* ═══════════ FINAL CTA ═══════════ */}
          <div className="mt-16 bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-2xl p-8 text-center text-white">
            <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">
              Optimiza las compras de tu restaurante con PLEKO
            </h2>
            <p className="text-indigo-100 mb-6 max-w-xl mx-auto">
              Gestiona proveedores, compara precios de mercado, escanea albaranes con IA y planifica pedidos automaticamente desde tus escandallos. Prueba gratis 14 dias.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-indigo-700 font-bold rounded-xl hover:bg-indigo-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-indigo-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
